import React, { useActionState, useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { ADD_USERS_NO_EMAIL } from 'common/constants/user';
import { useResponse } from 'services/hooks';
import useCreateBulkCompanyUsers from 'services/hooks/useCreateBulkCompanyUsers/useCreateBulkCompanyUsers';
import { useCreateBulkUsers } from 'services/hooks/useManageUser/useManageUser';
import { ActionButtonGroupProps } from './ActionButtonGroup.props';
import { actionButtonGroupClasses, ActionButtonGroupRoot } from './ActionButtonGroup.styles';

type FormState = { message: string; status: 'idle' | 'success' | 'error' };

const ActionButtonGroup = ({
  handleDialogClose,
  createButtonText,
  disableCreate,
  firm,
  selectedCompany,
  newUsers,
  companyList,
  fundList,
}: ActionButtonGroupProps) => {
  const { successNotification, errorNotification } = useResponse();
  const { mutate: createBulkUsers } = useCreateBulkUsers();
  const { mutate: createBulkCompanyUsers } = useCreateBulkCompanyUsers();
  const [skipEmail, setSkipEmail] = useState(false);

  const [, formAction, isPending] = useActionState<FormState, FormData>(
    async () => {
      try {
        await new Promise<void>((resolve, reject) => {
          if (selectedCompany) {
            createBulkCompanyUsers(
              {
                companyId: selectedCompany.id,
                users: newUsers ?? [],
                sendActivationEmail: !skipEmail,
              },
              {
                onSuccess: () => resolve(),
                onError: (err: unknown) =>
                  reject(err instanceof Error ? err : new Error('Failed to create company users')),
              }
            );
          } else {
            const { id: firmId } = firm;
            createBulkUsers(
              {
                firm_id: firmId,
                users: newUsers ?? [],
                companyList,
                fundList,
                skipEmail,
              },
              {
                onSuccess: () => resolve(),
                onError: (err: unknown) => reject(err instanceof Error ? err : new Error('Failed to create users')),
              }
            );
          }
        });
        handleDialogClose();
        successNotification('Users created successfully');
        return { message: 'Users created successfully', status: 'success' as const };
      } catch {
        errorNotification('Error creating users');
        return { message: 'Error creating users', status: 'error' as const };
      }
    },
    { message: '', status: 'idle' }
  );

  return (
    <ActionButtonGroupRoot>
      <form action={formAction}>
        <FormGroup row style={{ marginTop: '1rem', fontSize: '0.5rem' }}>
          <FormControlLabel
            control={
              <Checkbox
                name="add-users-no-email"
                color="primary"
                onClick={event => setSkipEmail((event.target as HTMLInputElement).checked)}
                disabled={isPending}
              />
            }
            label={<Typography variant="body2">{ADD_USERS_NO_EMAIL}</Typography>}
          />
        </FormGroup>
        <div className={actionButtonGroupClasses.buttonGroup}>
          <Button
            variant="outlined"
            color="primary"
            style={{ marginRight: '1rem' }}
            onClick={() => handleDialogClose()}
            disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={disableCreate || isPending}>
            {createButtonText}
            {isPending && <CircularProgress size={20} color="inherit" style={{ marginLeft: 8 }} />}
          </Button>
        </div>
      </form>
    </ActionButtonGroupRoot>
  );
};

export default ActionButtonGroup;
